import React, { useState, useEffect, useRef } from 'react';
import { useParams, useHistory, useLocation, Link } from 'react-router-dom';
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import BackLink from '../components/BackLink';
import LogoSpinner from '../components/ui/LogoSpinner';
import Dialog from '../components/ui/Dialog';
import EmptyState from '../components/ui/EmptyState';
import Panel from '../components/ui/Panel';
import { ListRow, ListStack } from '../components/ui/ListRow';
import PageLayout from '../components/ui/PageLayout';
import { buttonClass } from '../components/ui/Button';
import Tooltip from '../components/ui/Tooltip';
import { Field as KitField, TextArea } from '../components/ui/Field';
import Select from '../components/ui/Select';
import Tabs from '../components/ui/Tabs';
import Tag from '../components/ui/Tag';
import Reveal from '../components/ui/Reveal';
import Appear from '../components/ui/Appear';
import { CATEGORY, alpha } from '../components/ui/categoryColors';
import Checkbox from '../components/ui/Checkbox';
import StatusPill, { RelationTag } from '../Sales/StatusPill';
import { formatEventDate } from '../utils/date-utils';
import dayjs from 'dayjs';
import { Customer, CustomerNote, LeaseAdjustment, EffectiveLeaseCounts, CustomerRental, OpeningBalance, CustomerOrders, getStatusBadges } from './types';
import CustomerAssetsTab from './CustomerAssetsTab';
import CustomerOrdersTable from './CustomerOrdersTable';
import CustomerTotals from './CustomerTotals';

type CustomerTab = 'overview' | 'assets';

interface FieldProps {
  label: string;
  value?: any;
  children?: React.ReactNode;
  editing: boolean;
  onChange?: (v: any) => void;
  type?: string;
  options?: { value: string; label: string }[];
}

const Field: React.FC<FieldProps> = ({ label, value, children, editing, onChange, type = 'text', options }) => (
  <div className="flex flex-col sm:flex-row sm:items-baseline py-2 border-b border-gray-700/30 last:border-b-0 gap-1">
    <span className="text-on-surface-variant text-sm sm:w-40 flex-shrink-0">{label}</span>
    {editing ? (
      options ? (
        <Select label={label} value={value || ''} onChange={key => onChange?.(key)} className="flex-1"
          options={[{ key: '', label: '--' }, ...options.map(o => ({ key: o.value, label: o.label }))]} />
      ) : type === 'textarea' ? (
        <TextArea aria-label={label} value={value || ''} onChange={e => onChange?.(e.target.value)} rows={2} className="flex-1" />
      ) : (
        <KitField aria-label={label} type={type} value={value ?? ''} onChange={e => onChange?.(type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value)}
          className="flex-1" />
      )
    ) : (
      <span className="text-on-surface text-sm flex-1 whitespace-pre-wrap">{children || value || '\u2014'}</span>
    )}
  </div>
);

const EQUIPMENT_FIELDS = [
  { key: 'ls_drone_count', label: 'LS Drones', color: 'text-white' },
  { key: 'pyro_drone_count', label: 'Pyro Drones', color: 'text-on-surface', ink: CATEGORY.orange.ink.oklch },
  { key: 'battery_count', label: 'Batteries', color: 'text-on-surface', ink: CATEGORY.blue.ink.oklch },
  { key: 'charger_count', label: 'Chargers', color: 'text-on-surface' },
  { key: 'ground_station_count', label: 'Ground Stations', color: 'text-on-surface' },
  { key: 'gear_pelican_count', label: 'Gear Pelicans', color: 'text-on-surface' },
] as const;

const BALANCE_TYPES = [
  { type: 'purchased' as const, label: 'Purchased' },
  // Leased and rented opening balances are not supported — use lease/rental orders instead
];

const emptyBalance = (type: OpeningBalance['balance_type']): Omit<OpeningBalance, 'id' | 'organization_id' | 'created_at' | 'updated_at'> => ({
  balance_type: type,
  ls_drone_count: 0, pyro_drone_count: 0, battery_count: 0,
  charger_count: 0, ground_station_count: 0, gear_pelican_count: 0,
});

const OpeningBalanceSection: React.FC<{
  customerId: number;
  initialBalances: OpeningBalance[];
  editing: boolean;
  api: any;
  toast: any;
}> = ({ customerId, initialBalances, editing, api, toast }) => {
  const [balances, setBalances] = useState<OpeningBalance[]>(initialBalances);
  const [editBalances, setEditBalances] = useState<Record<string, any>>({});
  const [addingType, setAddingType] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setBalances(initialBalances);
    const map: Record<string, any> = {};
    for (const b of initialBalances) {
      map[b.balance_type] = { ...b };
    }
    setEditBalances(map);
  }, [initialBalances]);

  const existingTypes = new Set(balances.map(b => b.balance_type));
  const availableTypes = BALANCE_TYPES.filter(t => !existingTypes.has(t.type));

  const handleSave = async (type: string) => {
    const data = editBalances[type];
    if (!data) return;
    setSaving(true);
    try {
      const row = await api.put(`/customers/${customerId}/opening-balances/${type}`, { body: data });
      setBalances(prev => {
        const without = prev.filter(b => b.balance_type !== type);
        return [...without, row].sort((a, b) => a.balance_type.localeCompare(b.balance_type));
      });
      setAddingType(null);
      toast.success('Opening balance saved');
    } catch {
      toast.error('Failed to save opening balance');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (type: string) => {
    if (!window.confirm(`Remove ${type} opening balance?`)) return;
    try {
      await api.delete(`/customers/${customerId}/opening-balances/${type}`);
      setBalances(prev => prev.filter(b => b.balance_type !== type));
      setEditBalances(prev => { const copy = { ...prev }; delete copy[type]; return copy; });
      toast.success('Opening balance removed');
    } catch {
      toast.error('Failed to remove opening balance');
    }
  };

  const startAdd = (type: string) => {
    setEditBalances(prev => ({ ...prev, [type]: emptyBalance(type as OpeningBalance['balance_type']) }));
    setAddingType(type);
  };

  const setField = (type: string, field: string, value: number) => {
    setEditBalances(prev => ({
      ...prev,
      [type]: { ...(prev[type] || emptyBalance(type as OpeningBalance['balance_type'])), [field]: Math.max(0, value) },
    }));
  };

  const hasAnyBalance = balances.length > 0;
  const inputSmall = 'text-center font-bold';

  if (!editing && !hasAnyBalance) return null;

  return (
    <Panel
      title="Opening Balance"
      titleStyle="section"
      accent="primary"
      actions={
        editing && availableTypes.length > 0 && !addingType ? (
          <div className="flex gap-1.5">
            {availableTypes.map(t => (
              <button key={t.type} onClick={() => startAdd(t.type)}
                className={buttonClass('quiet', 'sm')}>
                + {t.label}
              </button>
            ))}
          </div>
        ) : undefined
      }
    >
      <p className="text-xs text-on-surface-variant mb-4 -mt-1">Starting counts carried over from the previous system. New orders build on top of these.</p>

      <Reveal show={balances.length === 0 && !addingType}>
        <EmptyState variant="area">No opening balances recorded</EmptyState>
      </Reveal>

      <div className="space-y-4">
        {[...balances.map(b => b.balance_type), ...(addingType && !existingTypes.has(addingType as any) ? [addingType] : [])].map(type => {
          const meta = BALANCE_TYPES.find(t => t.type === type)!;
          const isNew = !existingTypes.has(type as any);
          const data = editing
            ? (editBalances[type] || emptyBalance(type as OpeningBalance['balance_type']))
            : balances.find(b => b.balance_type === type);
          if (!meta || !data) return null;

          const hasValues = EQUIPMENT_FIELDS.some(f => (data[f.key] || 0) > 0);
          if (!editing && !hasValues) return null;

          return (
            <Panel key={type} on="panel" level={3} padding="sm" title={meta.label} titleStyle="section" accent="green"
              actions={editing ? (
                  <div className="flex gap-2">
                    <button onClick={() => handleSave(type)} disabled={saving}
                      className={buttonClass('good', 'sm')}>
                      {saving ? '...' : 'Save'}
                    </button>
                    {!isNew && (
                      <button onClick={() => handleDelete(type)}
                        className={buttonClass('danger-quiet', 'sm')}>
                        Remove
                      </button>
                    )}
                    {isNew && (
                      <button onClick={() => { setAddingType(null); setEditBalances(prev => { const copy = { ...prev }; delete copy[type]; return copy; }); }}
                        className={buttonClass('quiet', 'sm')}>
                        Cancel
                      </button>
                    )}
                  </div>
                ) : undefined}>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
                {EQUIPMENT_FIELDS.map(f => (
                  <div key={f.key} className="text-center">
                    <div className="text-[10px] text-on-surface-variant uppercase tracking-widest font-semibold mb-1">{f.label}</div>
                    {editing ? (
                      <KitField type="number" min="0" aria-label={f.label} value={data[f.key] ?? 0}
                        onChange={e => setField(type, f.key, Number(e.target.value) || 0)}
                        className={inputSmall} />
                    ) : (
                      <div
                        className={`text-xl font-bold ${(data[f.key] || 0) > 0 ? f.color : 'text-on-surface-disabled'}`}
                        style={(data[f.key] || 0) > 0 && 'ink' in f ? { color: f.ink } : undefined}
                      >
                        {data[f.key] || 0}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Panel>
          );
        })}
      </div>
    </Panel>
  );
};

const CustomerDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const location = useLocation();
  const api = useApi();
  const toast = useToast();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState<Partial<Customer>>({});
  const [saving, setSaving] = useState(false);

  // Tab state synced to ?tab=overview|assets (default overview). Letting the URL
  // own the state means deep links survive refresh and back/forward navigation.
  const activeTab: CustomerTab = (new URLSearchParams(location.search).get('tab') === 'assets') ? 'assets' : 'overview';
  const setActiveTab = (next: CustomerTab) => {
    const params = new URLSearchParams(location.search);
    if (next === 'overview') params.delete('tab'); else params.set('tab', next);
    const search = params.toString();
    history.replace({ pathname: location.pathname, search: search ? `?${search}` : '' });
  };

  // Orders, their shipments and the totals tiles: one fetch, one endpoint
  const [ordersData, setOrdersData] = useState<CustomerOrders | null>(null);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [ordersFailed, setOrdersFailed] = useState(false);

  // Lease data
  const [leaseAdjustments, setLeaseAdjustments] = useState<LeaseAdjustment[]>([]);
  const [effectiveCounts, setEffectiveCounts] = useState<EffectiveLeaseCounts | null>(null);

  // Rental data
  const [customerRentals, setCustomerRentals] = useState<CustomerRental[]>([]);

  // Notes
  const [notes, setNotes] = useState<CustomerNote[]>([]);
  const [newNote, setNewNote] = useState('');
  const [sendingNote, setSendingNote] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const notesEndRef = useRef<HTMLDivElement>(null);

  // The Notes dialog is phone-only; the sidebar panel takes over at lg.
  useEffect(() => {
    if (!chatOpen || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia('(min-width: 1024px)');
    const onChange = (e: MediaQueryListEvent) => { if (e.matches) setChatOpen(false); };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [chatOpen]);

  useEffect(() => {
    const fetchCustomer = async () => {
      try {
        const data = await api.get(`/customers/${id}`);
        setCustomer(data);
        setEditData(data);
      } catch (error) {
        toast.error('Customer not found');
        history.push('/customers');
      } finally {
        setLoading(false);
      }
    };
    fetchCustomer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadOrders = () => {
    if (!id) return;
    api.get(`/customers/${id}/orders`)
      .then(data => { setOrdersData(data); setOrdersFailed(false); })
      .catch(() => setOrdersFailed(true))
      .finally(() => setOrdersLoading(false));
  };

  useEffect(() => {
    loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Fetch lease and rental data
  useEffect(() => {
    if (!id) return;
    api.get(`/orders/lease-adjustments?org_id=${id}`).then(data => setLeaseAdjustments(data || [])).catch(() => {});
    api.get(`/orders/lease-effective?org_id=${id}`).then(data => setEffectiveCounts(data || null)).catch(() => {});
    api.get(`/orders/rentals-by-customer?org_id=${id}`).then(data => setCustomerRentals(data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!id) return;
    api.get(`/customers/${id}/notes`).then(data => setNotes(data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await api.put(`/customers/${id}`, { body: editData });
      setCustomer(prev => prev ? { ...prev, ...updated } : prev);
      setEditData(prev => ({ ...prev, ...updated }));
      setEditing(false);
      toast.success('Customer updated');
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!customer || !window.confirm(`Delete ${customer.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/customers/${id}`);
      toast.success('Customer deleted');
      history.push('/customers');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const set = (key: string, val: string | number | boolean) => setEditData(prev => ({ ...prev, [key]: val }));

  const handleSendNote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newNote.trim() || sendingNote) return;
    setSendingNote(true);
    try {
      const note = await api.post(`/customers/${id}/notes`, { body: { message: newNote.trim() } });
      setNotes(prev => [...prev, note]);
      setNewNote('');
      setTimeout(() => notesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch {
      toast.error('Failed to send note');
    } finally {
      setSendingNote(false);
    }
  };

  if (loading) return <LogoSpinner label="Loading the customer" />;
  if (!customer) return null;


  const ed = editing ? editData : customer;
  const badges = getStatusBadges(customer);

  return (
    <PageLayout kind="record" className="pt-6">
    <div className="space-y-5">
      {/* Back */}
      <BackLink to="/customers" label="Customers" />

      {/* Header */}
      <Panel label="Customer" accent="primary" lit>
        <div className="pt-1 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight leading-tight">
              {customer.name}
            </h1>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              {customer.customer_code && (
                <Tooltip label="Customer ID">
                  <Tag variant="code" mono className="gap-1.5">
                    <span className="font-sans text-[10px] font-semibold uppercase tracking-widest text-on-surface-variant">ID</span>
                    {customer.customer_code}
                  </Tag>
                </Tooltip>
              )}
              {badges.map(badge => (
                <RelationTag key={badge} badge={badge} />
              ))}
              {customer.contact_name && (
                <span className="text-on-surface-variant text-sm">{customer.contact_name}</span>
              )}
              {customer.phone && (
                <span className="text-on-surface-variant text-sm font-mono">{customer.phone}</span>
              )}
            </div>
          </div>
        </div>

        <div className="my-4" />

        <div className="flex flex-wrap gap-2">
          {editing ? (
            <>
              <button onClick={handleSave} disabled={saving}
                className={buttonClass('good', 'md')}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              <button onClick={() => { setEditing(false); setEditData(customer); }}
                className={buttonClass('quiet', 'md')}>
                Cancel
              </button>
            </>
          ) : (
            <>
              <Link to={`/orders/new?type=sale&customer=${customer.id}`}
                className={buttonClass('good', 'md')}>
                + Sale
              </Link>
              <Link to={`/orders/new?type=lease&customer=${customer.id}`}
                className={buttonClass('info', 'md')}>
                + Lease
              </Link>
              <Link to={`/orders/new?type=rental&customer=${customer.id}`}
                className={buttonClass('warn', 'md')}>
                + Rental
              </Link>
              <div className="flex-1" />
              <button onClick={() => setEditing(true)}
                className={buttonClass('accent', 'md')}>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" /></svg>
                Edit
              </button>
              <button onClick={handleDelete}
                className={buttonClass('danger-quiet', 'md')}>
                Delete
              </button>
            </>
          )}
        </div>
      </Panel>

      {/* Tab strip */}
      <Tabs
        id="customer"
        label="Customer sections"
        value={activeTab}
        onChange={key => setActiveTab(key as CustomerTab)}
        tabs={[{ key: 'overview', label: 'Overview' }, { key: 'assets', label: 'Assets' }]}
      />

      {activeTab === 'assets' && (
        <div role="tabpanel" id="customer-panel-assets" aria-labelledby="customer-tab-assets">
          <CustomerAssetsTab customerId={customer.id} />
        </div>
      )}

      {activeTab === 'overview' && (
      <div role="tabpanel" id="customer-panel-overview" aria-labelledby="customer-tab-overview">
      {/* Totals, then the orders they come from: the full page width, above the notes sidebar */}
      <div className="mb-5 space-y-5">
        {ordersData && <CustomerTotals totals={ordersData.totals} orders={ordersData.orders} />}
        <CustomerOrdersTable
          customerId={customer.id}
          data={ordersData}
          loading={ordersLoading}
          failed={ordersFailed}
          onChanged={loadOrders}
        />
      </div>

      {/* Two-column layout: main content + notes sidebar */}
      <div className="flex flex-col lg:flex-row gap-5">

      {/* ── LEFT: Main Content ── */}
      <div className="flex-1 min-w-0 space-y-5">

      {/* Details grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Contact & Address */}
        <Panel title="Contact & Address" titleStyle="section" accent="primary">
          <Field label="Contact" value={ed.contact_name} editing={editing} onChange={v => set('contact_name', v)} />
          <Field label="Phone" value={ed.phone} editing={editing} onChange={v => set('phone', v)} />
          <Field label="Email" value={ed.contact_email} editing={editing} onChange={v => set('contact_email', v)} />
          <Field label="Shipping Address" value={ed.shipping_address} editing={editing} onChange={v => set('shipping_address', v)} type="textarea" />
        </Panel>

        {/* Order Details */}
        <Panel title="Order Details" titleStyle="section" accent="primary">
          {/* Status checkboxes (edit mode) or badges (view mode) */}
          <div className="flex flex-col sm:flex-row sm:items-baseline py-2 border-b border-gray-700/30 gap-1">
            <span className="text-on-surface-variant text-sm sm:w-40 flex-shrink-0">Customer Type</span>
            {editing ? (
              <div className="flex flex-wrap gap-4">
                {([
                  { key: 'is_purchaser', label: 'Purchase' },
                  { key: 'is_lessee', label: 'Lease' },
                  { key: 'is_renter', label: 'Rental' },
                ] as const).map(opt => (
                  <Checkbox
                    key={opt.key}
                    label={opt.label}
                    checked={ed[opt.key] || false}
                    onChange={e => set(opt.key, e.target.checked)}
                  />
                ))}
              </div>
            ) : (
              <div className="flex gap-2">
                {badges.length > 0 ? badges.map(badge => (
                  <RelationTag key={badge} badge={badge} />
                )) : <span className="text-on-surface-variant text-sm">{'\u2014'}</span>}
              </div>
            )}
          </div>
          <Field label="SW & Support" value={ed.software_terms} editing={editing} onChange={v => set('software_terms', v)} />
          <Field label="Plastics" value={ed.plastics_version} editing={editing} onChange={v => set('plastics_version', v)}
            options={[{ value: 'Original', label: 'Original' }, { value: 'New', label: 'New' }]} />
          <Field label="Waiver" value={ed.waiver_type} editing={editing} onChange={v => set('waiver_type', v)} />
        </Panel>
      </div>

      {/* Opening Balance (cutover from previous system — edit mode only) */}
      <Reveal show={editing}>
        <OpeningBalanceSection
          customerId={customer.id}
          initialBalances={customer.opening_balances || []}
          editing={editing}
          api={api}
          toast={toast}
        />
      </Reveal>

      {/* Lease Timeline */}
      {(customer.is_lessee || leaseAdjustments.length > 0) && (
        <Panel
          title="Lease Timeline"
          titleStyle="section"
          accent="primary"
          actions={
            <Link to={`/orders/new?type=lease&customer=${customer.id}`}
              className={buttonClass('info', 'sm')}>
              + New Lease Order
            </Link>
          }
        >

          {/* Effective counts summary */}
          {effectiveCounts && Object.values(effectiveCounts).some(v => v > 0) && (
            <div
              role="group"
              aria-label="Current effective lease"
              className="mb-4 rounded-control p-3"
              style={{ backgroundColor: alpha(CATEGORY.blue.mark, 0.1) }}
            >
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-widest" style={{ color: CATEGORY.blue.ink.oklch }}>Current effective lease</div>
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                {([
                  ['ls_drones', 'LS Drones', undefined],
                  ['pyro_drones', 'Pyro', CATEGORY.orange.ink.oklch],
                  ['batteries', 'Batteries', CATEGORY.blue.ink.oklch],
                  ['chargers', 'Chargers', undefined],
                  ['ground_stations', 'Ground Stations', undefined],
                  ['gear_pelicans', 'Gear Pelicans', undefined],
                  ['drone_carriers', 'Drone Carriers', undefined],
                ] as const).map(([key, label, ink]) =>
                  (effectiveCounts[key] ?? 0) > 0 ? (
                    <div key={key}>
                      <span className="text-xl font-bold text-on-surface" style={ink ? { color: ink } : undefined}>{effectiveCounts[key]}</span>{' '}
                      <span className="text-xs text-on-surface-variant">{label}</span>
                    </div>
                  ) : null
                )}
              </div>
            </div>
          )}

          {/* Adjustments list */}
          <ListStack
            on="panel"
            label="Lease adjustments"
            items={leaseAdjustments}
            getKey={adj => String(adj.id)}
            empty={<EmptyState variant="line">No lease adjustments yet</EmptyState>}
            renderRow={adj => {
              const isPermanent = !adj.end_date;
              const isCancelled = adj.status === 'cancelled';
              const counts = [
                adj.ls_drone_count > 0 && `${adj.ls_drone_count} LS`,
                adj.pyro_drone_count > 0 && `${adj.pyro_drone_count} Pyro`,
                adj.battery_count > 0 && `${adj.battery_count} Batt`,
                adj.charger_count > 0 && `${adj.charger_count} Chg`,
                adj.ground_station_count > 0 && `${adj.ground_station_count} GS`,
                adj.gear_pelican_count > 0 && `${adj.gear_pelican_count} Pelican`,
                (adj.drone_carrier_count || 0) > 0 && `${adj.drone_carrier_count} Carrier`,
              ].filter(Boolean).join(', ');
              return (
                <ListRow
                  className={isCancelled ? 'opacity-40' : ''}
                  leading={
                    <StatusPill status={adj.status} />
                  }
                  title={counts}
                  meta={
                    <>
                      {formatEventDate(adj.effective_date)}
                      {isPermanent ? ' — Permanent' : ` — ${formatEventDate(adj.end_date)}`}
                      {adj.so_number && (
                        <Link to={`/orders/${adj.order_id}`} className="ml-2 text-blue-400 hover:text-blue-300">
                          {adj.so_number}
                        </Link>
                      )}
                    </>
                  }
                />
              );
            }}
          />
        </Panel>
      )}

      {/* Rentals */}
      {(customer.is_renter || customerRentals.length > 0) && (
        <Panel
          title="Rentals"
          titleStyle="section"
          accent="primary"
          actions={
            <Link to={`/orders/new?type=rental&customer=${customer.id}`}
              className={buttonClass('warn', 'sm')}>
              + New Rental
            </Link>
          }
        >

          <ListStack
            on="panel"
            label="Rentals"
            items={customerRentals}
            getKey={rental => String(rental.id)}
            empty={<EmptyState variant="line">No rentals yet</EmptyState>}
            renderRow={rental => {
              const totalDrones = (rental.lines || []).filter(l => l.item_type === 'drone_ls' || l.item_type === 'drone_pyro').reduce((s, l) => s + l.quantity, 0);
              const totalBatteries = (rental.lines || []).filter(l => l.item_type === 'battery').reduce((s, l) => s + l.quantity, 0);
              return (
                <ListRow
                  to={`/orders/${rental.id}`}
                  leading={
                    <StatusPill status={rental.status} />
                  }
                  title={
                    <>
                      {rental.so_number}
                      {totalDrones > 0 && <span className="ml-2 text-on-surface-variant">{totalDrones} drones</span>}
                      {totalBatteries > 0 && <span className="ml-2 text-on-surface-variant">{totalBatteries} batt</span>}
                    </>
                  }
                  meta={`${formatEventDate(rental.effective_date)} — ${formatEventDate(rental.end_date)}`}
                />
              );
            }}
          />
        </Panel>
      )}

      {/* Notes (static field) */}
      <Panel title="Notes" titleStyle="section" accent="primary">
        {editing ? (
          <TextArea aria-label="Notes" value={ed.notes || ''} onChange={e => set('notes', e.target.value)} rows={3} />
        ) : (
          <p className="text-on-surface text-sm whitespace-pre-wrap leading-relaxed">{customer.notes || 'No notes'}</p>
        )}
      </Panel>

      {/* Customer Location */}
      {(customer.latitude && customer.longitude) && (
        <Panel title="Location" titleStyle="section" accent="primary">
          <div className="text-on-surface-variant text-sm">
            {customer.latitude.toFixed(4)}, {customer.longitude.toFixed(4)}
            {customer.shipping_address && <span className="ml-2 text-on-surface-variant">({customer.shipping_address})</span>}
          </div>
        </Panel>
      )}

      </div>{/* end left column */}

      {/* ── RIGHT: Notes Sidebar (desktop) ── */}
      <div className="hidden lg:block lg:w-80 lg:flex-shrink-0 self-start lg:sticky lg:top-4">
        <Panel title="Notes" titleStyle="section" accent="primary" className="max-h-[600px]">
          <div className="flex-1 overflow-y-auto space-y-1 mb-4 min-h-0">
            <Reveal show={notes.length === 0}>
              <EmptyState variant="line">No notes yet</EmptyState>
            </Reveal>
            {notes.map((note, i) => {
              const prev = notes[i - 1];
              const sameSender = prev && prev.user_id === note.user_id;
              const sameBlock = sameSender && (new Date(note.created_at).getTime() - new Date(prev.created_at).getTime()) < 5 * 60 * 1000;
              return (
                <div key={note.id} className={`px-1 py-0.5 hover:bg-gray-700/20 rounded ${!sameBlock && i > 0 ? 'mt-3' : ''}`}>
                  {!sameBlock && (
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm font-semibold text-on-surface">{note.firstname} {note.lastname}</span>
                      <span className="text-[10px] text-on-surface-variant">{dayjs(note.created_at).format('MMM D, h:mm A')}</span>
                    </div>
                  )}
                  <p className="text-sm text-on-surface whitespace-pre-wrap break-words">{note.message_text}</p>
                </div>
              );
            })}
            <div ref={notesEndRef} />
          </div>
          <form onSubmit={handleSendNote} className="flex gap-2 flex-shrink-0">
            <KitField type="text" value={newNote} onChange={e => setNewNote(e.target.value)}
              placeholder="Add a note..." aria-label="Add a note" className="flex-1" />
            <button type="submit" disabled={!newNote.trim() || sendingNote}
              className={`${buttonClass('tonal', 'md')} shrink-0`}>
              {sendingNote ? '...' : 'Send'}
            </button>
          </form>
        </Panel>
      </div>

      {/* ── Mobile: Floating notes button + fullscreen modal ── */}
      <div className="lg:hidden">
        {!chatOpen && (
          <button onClick={() => setChatOpen(true)}
            className="fixed bottom-6 right-6 z-40 w-14 h-14 bg-primary rounded-full shadow-lg flex items-center justify-center hover:bg-primary/90 transition-colors">
            <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <Appear show={notes.length > 0} className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
              {notes.length}
            </Appear>
          </button>
        )}
        <Dialog open={chatOpen} onClose={() => setChatOpen(false)} title="Notes" size="md" closeHint="Close notes">
          <div className="flex flex-col">
            <div className="space-y-1 mb-4">
              {notes.length === 0 && <EmptyState variant="line">No notes yet</EmptyState>}
              {notes.map((note, i) => {
                const prev = notes[i - 1];
                const sameSender = prev && prev.user_id === note.user_id;
                const sameBlock = sameSender && (new Date(note.created_at).getTime() - new Date(prev.created_at).getTime()) < 5 * 60 * 1000;
                return (
                  <div key={note.id} className={`px-1 py-0.5 hover:bg-gray-700/20 rounded ${!sameBlock && i > 0 ? 'mt-3' : ''}`}>
                    {!sameBlock && (
                      <div className="flex items-baseline gap-2">
                        <span className="text-sm font-semibold text-on-surface">{note.firstname} {note.lastname}</span>
                        <span className="text-[10px] text-on-surface-variant">{dayjs(note.created_at).format('MMM D, h:mm A')}</span>
                      </div>
                    )}
                    <p className="text-sm text-on-surface whitespace-pre-wrap break-words">{note.message_text}</p>
                  </div>
                );
              })}
              <div ref={notesEndRef} />
            </div>
            <form onSubmit={handleSendNote} className="flex gap-2 flex-shrink-0">
              <KitField type="text" value={newNote} onChange={e => setNewNote(e.target.value)}
                placeholder="Add a note..." aria-label="Add a note" className="flex-1" />
              <button type="submit" disabled={!newNote.trim() || sendingNote}
                className={`${buttonClass('tonal', 'md')} shrink-0`}>
                {sendingNote ? '...' : 'Send'}
              </button>
            </form>
          </div>
        </Dialog>
      </div>

      </div>{/* end two-column flex */}
      </div>
      )}
    </div>
    </PageLayout>
  );
};

export default CustomerDetail;
